// src/components/CompareCell.js

import React from 'react';

const PLACEHOLDER_IMAGE =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="120" height="120">' +
    '<rect width="120" height="120" fill="#f3f4f6"/>' +
    '<text x="60" y="60" font-family="sans-serif" font-size="11" fill="#9ca3af" text-anchor="middle" dominant-baseline="middle">No image</text>' +
    '</svg>'
  );

export default function CompareCell({
  product,
  chain,
  isShortlisted,
  isAnchored,
  isMatched,
  onShortlist,
  onUnshortlist,
  onAnchor,
  onRelease,
}) {
  const unitLabel = product.cupPrice
    ? product.cupPrice.replace(/\s+/g, ' ').trim()
    : product.pricePer100g
      ? `$${product.pricePer100g.toFixed(2)} / 100g`
      : null;

  const avg = product.stats?.avg30d;
  const hasAvg = avg != null && avg > 0;
  const saving = hasAvg && avg > product.price ? avg - product.price : 0;
  const savingPct = saving > 0 ? Math.round((saving / avg) * 100) : 0;

  const borderClass = isAnchored
    ? 'border-green-500 ring-2 ring-green-400'
    : isMatched
      ? 'border-blue-400 ring-1 ring-blue-300'
      : 'border-gray-200';

  return (
    <div className={`flex flex-col border rounded-lg overflow-hidden bg-white ${borderClass}`}>
      <div className="aspect-square bg-gray-50 flex items-center justify-center overflow-hidden relative">
        <img
          src={product.imageUrl || PLACEHOLDER_IMAGE}
          alt={product.name}
          className="w-full h-full object-contain"
          loading="lazy"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = PLACEHOLDER_IMAGE;
          }}
        />
        {isAnchored && (
          <div className="absolute top-1 right-1 bg-green-600 text-white text-[10px] px-1.5 py-0.5 rounded font-semibold">
            HELD
          </div>
        )}
        {isMatched && (
          <div className="absolute top-1 right-1 bg-blue-500 text-white text-[10px] px-1.5 py-0.5 rounded font-semibold">
            MATCH
          </div>
        )}
        {isShortlisted && !isAnchored && !isMatched && (
          <div className="absolute top-1 right-1 bg-green-600 text-white text-[10px] px-1.5 py-0.5 rounded font-semibold">
            ✓
          </div>
        )}
      </div>
      <div className="p-2 flex flex-col flex-1">
        <div className="text-xs font-medium text-gray-800 line-clamp-2 leading-tight min-h-[2.2em]">
          {product.name}
        </div>
        <div className="mt-1 text-sm font-semibold text-gray-900">
          ${product.price.toFixed(2)}
        </div>
        {hasAvg && (
          <div className="text-[10px] text-gray-500 leading-tight">
            avg ${avg.toFixed(2)}
          </div>
        )}
        {saving > 0 && (
          <div className="text-[10px] text-green-700 font-medium leading-tight">
            save ${saving.toFixed(2)} ({savingPct}%)
          </div>
        )}
        {unitLabel && (
          <div className="text-[10px] text-gray-500 leading-tight">{unitLabel}</div>
        )}

        <div className="mt-1 flex gap-1">
          {isAnchored ? (
            <button
              type="button"
              onClick={onRelease}
              className="flex-1 py-1 rounded text-[10px] font-medium bg-green-100 text-green-800 hover:bg-green-200"
            >
              Release
            </button>
          ) : (
            <button
              type="button"
              onClick={() => onAnchor(product.productId, chain)}
              className="flex-1 py-1 rounded text-[10px] font-medium bg-gray-100 text-gray-700 hover:bg-gray-200"
            >
              Anchor
            </button>
          )}
        </div>

        <button
          type="button"
          onClick={() =>
            isShortlisted ? onUnshortlist(product.productId) : onShortlist(product.productId, product, chain)
          }
          className={`mt-1 w-full py-1 rounded text-xs font-medium ${
            isShortlisted
              ? 'bg-green-100 text-green-800 hover:bg-green-200'
              : 'bg-green-600 text-white hover:bg-green-700'
          }`}
        >
          {isShortlisted ? 'Shortlisted ✓' : 'Shortlist'}
        </button>
      </div>
    </div>
  );
}
